import { RpcError } from "grpc-web";
import {
  AcceptGuestTermsRequest,
  AcceptGuestTermsResponse,
  CheckGuestSessionRequest,
  CheckGuestSessionResponse,
  CreateGuestSessionRequest,
  CreateGuestSessionResponse,
  ClaimGuestSessionRequest,
  ClaimGuestSessionResponse,
} from "../proto/api_pb";
import { OmmuseClient } from "../proto/api_grpc_web_pb";
import { markGuestSession } from "../components/Guest";

const GUEST_TOKEN_KEY = "ommuse.guest.token";
const GUEST_SESSION_ID_KEY = "ommuse.guest.session_id";
const GUEST_EXPIRES_AT_KEY = "ommuse.guest.expires_at_ms";
const GUEST_QUOTA_BYTES_KEY = "ommuse.guest.quota_bytes";

export interface GuestSession {
  token: string;
  sessionId: string;
  expiresAtMs: number;
  quotaBytes: number;
}

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* noop */
  }
};

const safeRemove = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* noop */
  }
};

export const getStoredGuestSession = (): GuestSession | null => {
  const token = safeGet(GUEST_TOKEN_KEY);
  const sessionId = safeGet(GUEST_SESSION_ID_KEY);
  const expiresRaw = safeGet(GUEST_EXPIRES_AT_KEY);
  const quotaRaw = safeGet(GUEST_QUOTA_BYTES_KEY);
  if (!token || !sessionId || !expiresRaw) return null;
  const expiresAtMs = Number(expiresRaw);
  if (!Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now()) return null;
  return {
    token,
    sessionId,
    expiresAtMs,
    quotaBytes: quotaRaw ? Number(quotaRaw) : 100 * 1024 * 1024,
  };
};

export const clearStoredGuestSession = () => {
  safeRemove(GUEST_TOKEN_KEY);
  safeRemove(GUEST_SESSION_ID_KEY);
  safeRemove(GUEST_EXPIRES_AT_KEY);
  safeRemove(GUEST_QUOTA_BYTES_KEY);
};

const storeGuestSession = (s: GuestSession) => {
  safeSet(GUEST_TOKEN_KEY, s.token);
  safeSet(GUEST_SESSION_ID_KEY, s.sessionId);
  safeSet(GUEST_EXPIRES_AT_KEY, String(s.expiresAtMs));
  safeSet(GUEST_QUOTA_BYTES_KEY, String(s.quotaBytes));
};

/**
 * Mint a fresh guest session against the API and persist the token to
 * localStorage. The caller is expected to navigate the user into the app
 * shell on success. Terms acceptance is collected later at first upload via
 * acceptGuestTerms().
 */
export const createGuestSession = async (): Promise<GuestSession> => {
  const client = new OmmuseClient(process.env.REACT_APP_API_URL!);
  const req = new CreateGuestSessionRequest();
  // Field is kept for backward compatibility; the server ignores it.
  req.setAcceptedTerms(false);

  const response = await new Promise<CreateGuestSessionResponse>(
    (resolve, reject) => {
      client.createGuestSession(req, {}, (err: RpcError | null, resp) => {
        if (err) reject(err);
        else resolve(resp);
      });
    }
  );

  const session: GuestSession = {
    token: response.getToken(),
    sessionId: response.getSessionId(),
    expiresAtMs: response.getExpiresAtMs(),
    quotaBytes: response.getQuotaBytes(),
  };
  storeGuestSession(session);
  markGuestSession();
  return session;
};

/**
 * Send the stored guest token alongside an Auth0 access token so the server
 * can merge the guest's tracks/projects into the just-signed-up account.
 * Returns null when there is nothing to claim (incognito browser, expired
 * session, etc.) so the caller can branch on that without throwing.
 */
export const claimGuestSession = async (
  auth0Token: string
): Promise<ClaimGuestSessionResponse | null> => {
  const stored = getStoredGuestSession();
  if (!stored) return null;
  const client = new OmmuseClient(process.env.REACT_APP_API_URL!);
  const req = new ClaimGuestSessionRequest();
  req.setGuestToken(stored.token);
  const metadata = { Authorization: auth0Token };
  try {
    return await new Promise<ClaimGuestSessionResponse>((resolve, reject) => {
      client.claimGuestSession(req, metadata, (err: RpcError | null, resp) => {
        if (err) reject(err);
        else resolve(resp);
      });
    });
  } finally {
    // Whether the claim succeeded or not, the token can't be re-used: the
    // user is now Auth0-authenticated and the server has either consumed or
    // rejected the guest doc.
    clearStoredGuestSession();
  }
};

/**
 * Stamp the guest user document with terms-accepted-at. Called by the
 * GuestTermsModal when the guest checks the consent box and clicks Continue.
 * Requires the caller to attach the guest JWT as the Authorization header
 * (the auth interceptor allow-lists this RPC for guest tokens).
 */
export const acceptGuestTerms = async (): Promise<number> => {
  const stored = getStoredGuestSession();
  if (!stored) {
    throw new Error("No guest session in localStorage");
  }
  const client = new OmmuseClient(process.env.REACT_APP_API_URL!);
  const req = new AcceptGuestTermsRequest();
  const metadata = { Authorization: stored.token };
  const response = await new Promise<AcceptGuestTermsResponse>(
    (resolve, reject) => {
      client.acceptGuestTerms(req, metadata, (err: RpcError | null, resp) => {
        if (err) reject(err);
        else resolve(resp);
      });
    }
  );
  return response.getAcceptedAtMs();
};

/**
 * Probe whether the current IP has an active guest session on the server.
 * No side effects — no token is minted, no TTL is slid. Safe to call on
 * every Welcome page mount. Returns false on any network or server error so
 * the probe never blocks the page.
 */
export const checkGuestSession = async (): Promise<boolean> => {
  const client = new OmmuseClient(process.env.REACT_APP_API_URL!);
  const response = await new Promise<CheckGuestSessionResponse>(
    (resolve, reject) => {
      client.checkGuestSession(
        new CheckGuestSessionRequest(),
        {},
        (err: RpcError | null, resp) => {
          if (err) reject(err);
          else resolve(resp);
        }
      );
    }
  );
  return response.getHasActiveSession();
};
